import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

import type {DeniedDevices} from '@pexip/media';
import {
    isAudioDeviceInUse,
    isVideoDeviceInUse,
    UserMediaStatus,
} from '@pexip/media';
import type {ExtendedSizeModifier, GroupProps} from '@pexip/components';
import {Button, Icon, IconTypes, Group} from '@pexip/components';

import {TestId} from '../../../test/testIds';
import type {
    BlockedBrowserPermissionsInfoType,
    DeviceStatusInfo,
} from '../../types';
import type {TooltipPosition} from '../InputControl/PreflightInputControl.view';
import {PreflightInputControl} from '../InputControl/PreflightInputControl.view';
import {useInputsState} from '../../hooks/useInputsState';
import {BackgroundBlurInputControl} from '../InputControl/InputControl.view';

export const PreflightInputControls: React.FC<
    GroupProps & {
        audioInputControl?: React.ReactNode;
        enhancerEnd?: React.ReactNode;
        inputStatusInfo: DeviceStatusInfo;
        isAudioInputMuted: boolean;
        isAudioInputUnavailable: boolean;
        isBlurred?: boolean;
        isMobileDevice?: boolean;
        isVideoInputMuted: boolean;
        isVideoInputUnavailable: boolean;
        requestMediaPermissions: () => void;
        segmentationEnabled?: boolean;
        silentTooltipInfo?: DeviceStatusInfo;
        streamStatus?: UserMediaStatus;
        toggleAudioInput?: () => void;
        toggleBlur?: () => void;
        toggleVideoInput?: () => void;
        toggleFacingMode?: () => void;
        deniedDevice?: DeniedDevices;
        setShowHelpVideo: React.Dispatch<React.SetStateAction<boolean>>;
        learnHowToGrantAccessURL?: string;
        permissionInfoType: BlockedBrowserPermissionsInfoType;
    }
> = ({
    audioInputControl,
    enhancerEnd,
    inputStatusInfo,
    isAudioInputMuted,
    isAudioInputUnavailable,
    isBlurred,
    isMobileDevice = false,
    isVideoInputMuted,
    isVideoInputUnavailable,
    requestMediaPermissions,
    segmentationEnabled,
    silentTooltipInfo,
    streamStatus,
    toggleAudioInput,
    toggleBlur,
    toggleVideoInput,
    toggleFacingMode,
    deniedDevice,
    setShowHelpVideo,
    learnHowToGrantAccessURL,
    permissionInfoType,
    ...props
}) => {
    const {t} = useTranslation();

    const {
        audioProps,
        videoProps,
        warning,
        isAudioIconMuted,
        isVideoIconMuted,
    } = useInputsState({
        isAudioInputMuted,
        isVideoInputMuted,
        inputStatusInfo,
        iconSize: 'medium',
    });

    const [isAudioTooltipVisible, setIsAudioTooltipVisible] = useState(
        warning === 'audio',
    );
    const [isVideoTooltipVisible, setIsVideoTooltipVisible] = useState(
        warning === 'audio-video',
    );

    useEffect(() => {
        if (silentTooltipInfo && !isVideoTooltipVisible) {
            setIsAudioTooltipVisible(true);
        } else {
            setIsAudioTooltipVisible(false);
        }
    }, [isVideoTooltipVisible, silentTooltipInfo]);

    useEffect(() => {
        setIsVideoTooltipVisible(
            warning === 'video' || warning === 'audio-video',
        );
    }, [warning]);

    useEffect(() => {
        setIsAudioTooltipVisible(warning === 'audio');
    }, [warning]);

    const [disableBgButton, setDisableBgButton] = useState(true);

    useEffect(() => {
        if (
            streamStatus &&
            ![
                UserMediaStatus.InitialPermissionsNotGranted,
                UserMediaStatus.InitialPermissionsGrantedVideoInputDenied,
            ].includes(streamStatus)
        ) {
            setDisableBgButton(
                isVideoInputMuted ||
                    warning === 'video' ||
                    warning === 'audio-video',
            );
        }
    }, [streamStatus, warning, isVideoInputMuted]);

    const coreInputProps: {
        size: ExtendedSizeModifier;
        tooltipPosition: TooltipPosition;
    } = {
        size: 'large',
        tooltipPosition: isMobileDevice ? 'topRight' : 'topLeft',
    };

    return (
        <Group
            spacing={isMobileDevice ? 'small' : 'medium'}
            justifyContent="center"
            {...props}
        >
            {!isMobileDevice && !!segmentationEnabled && (
                <BackgroundBlurInputControl
                    variant="transparentAlternative"
                    size="large"
                    isBlurred={isBlurred}
                    isDisabled={isVideoInputUnavailable || disableBgButton}
                    onClick={toggleBlur}
                    iconSize="medium"
                    data-testid={
                        isBlurred
                            ? TestId.ButtonBackgroundEffectsOn
                            : TestId.ButtonBackgroundEffectsOff
                    }
                />
            )}
            {toggleFacingMode && (
                <Button
                    aria-label={t('media.flip-camera', 'Flip camera')}
                    data-testid={TestId.ButtonFlipCamera}
                    modifier="square"
                    onClick={toggleFacingMode}
                    size="large"
                    variant="transparentAlternative"
                    disabled={isVideoInputMuted}
                >
                    <Icon source={IconTypes.IconFlipCamera} size="medium" />
                </Button>
            )}
            {enhancerEnd && enhancerEnd}
            {
                <PreflightInputControl
                    {...audioProps}
                    {...coreInputProps}
                    inputStatusInfo={silentTooltipInfo ?? inputStatusInfo}
                    isDisabled={isAudioInputUnavailable}
                    isTooltipVisible={
                        !isAudioInputUnavailable && isAudioTooltipVisible
                    }
                    setIsTooltipVisible={setIsAudioTooltipVisible}
                    toolTipTestId={TestId.NotificationToolTipAudioProblem}
                    data-testid={
                        isAudioIconMuted
                            ? TestId.ButtonAudioMeterMuted
                            : TestId.ButtonAudioMeter
                    }
                    onClick={toggleAudioInput}
                    requestMediaPermissions={requestMediaPermissions}
                    shouldShowRetry={
                        !!streamStatus &&
                        !silentTooltipInfo &&
                        isAudioDeviceInUse(streamStatus)
                    }
                    testIdCloseButton={
                        TestId.NotificationToolTipAudioCloseButton
                    }
                    variant="transparentAlternative"
                    audioInputControl={audioInputControl}
                    streamStatus={streamStatus}
                    deniedDevice={deniedDevice}
                    setShowHelpVideo={setShowHelpVideo}
                    learnHowToGrantAccessURL={learnHowToGrantAccessURL}
                    permissionInfoType={permissionInfoType}
                />
            }
            {
                <PreflightInputControl
                    {...videoProps}
                    {...coreInputProps}
                    inputStatusInfo={inputStatusInfo}
                    isDisabled={isVideoInputUnavailable}
                    isTooltipVisible={
                        !isVideoInputUnavailable && isVideoTooltipVisible
                    }
                    setIsTooltipVisible={setIsVideoTooltipVisible}
                    toolTipTestId={TestId.NotificationToolTipVideoProblem}
                    data-testid={
                        isVideoIconMuted
                            ? TestId.ButtonVideoinputMuted
                            : TestId.ButtonVideoInput
                    }
                    onClick={toggleVideoInput}
                    requestMediaPermissions={requestMediaPermissions}
                    testIdCloseButton={
                        TestId.NotificationToolTipVideoCloseButton
                    }
                    shouldShowRetry={
                        !!streamStatus && isVideoDeviceInUse(streamStatus)
                    }
                    variant="transparentAlternative"
                    streamStatus={streamStatus}
                    deniedDevice={deniedDevice}
                    setShowHelpVideo={setShowHelpVideo}
                    learnHowToGrantAccessURL={learnHowToGrantAccessURL}
                    permissionInfoType={permissionInfoType}
                />
            }
        </Group>
    );
};

export type PreflightInputControlsProps = React.ComponentProps<
    typeof PreflightInputControls
>;
